import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useQueryStore } from "../store/queryStore";
import { queryService } from "../services/queryService";
import { joinPresenceChannel, initializeEcho } from "../services/echoService";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Send,
  Paperclip,
  Image as ImageIcon,
  FileText,
  X,
  Download,
  CheckCircle2,
  Clock,
  User,
  Users,
  MoreVertical,
  XCircle,
  Loader2,
} from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";

function ChatWindow() {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);

  const selectedQuery = useQueryStore((state) => state.selectedQuery);
  const messages = useQueryStore((state) => state.messages);
  const setSelectedQuery = useQueryStore((state) => state.setSelectedQuery);
  const setMessages = useQueryStore((state) => state.setMessages);
  const addMessage = useQueryStore((state) => state.addMessage);
  const updateQueryStatus = useQueryStore((state) => state.updateQueryStatus);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageBody, setMessageBody] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [showActions, setShowActions] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addingUser, setAddingUser] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleRealtimeUpdate = useCallback(
    async (event) => {
      console.log("Chat real-time update:", event);
      console.log("Current queryId:", queryId);

      const eventQueryId = event.current_query_id || event.query_id;

      if (event.type === "QUERY_MESSAGE" && event.current_query_id == queryId) {
        console.log("Processing QUERY_MESSAGE event, reloading messages...");
        // Reload messages to get the latest data
        try {
          const data = await queryService.getMessages(queryId);
          setMessages(data);
          console.log("Messages reloaded successfully");
        } catch (error) {
          console.error("Failed to reload messages:", error);
        }

        // Play notification sound
        const audio = new Audio("/sounds/bell.mp3");
        audio.play().catch((e) => console.warn("Audio play failed:", e));
      } else if (
        event.type === "QUERY_MESSAGE_CLOSED" &&
        eventQueryId == queryId
      ) {
        updateQueryStatus(queryId, "CLOSED");
        setSelectedQuery((prev) =>
          prev ? { ...prev, status: "CLOSED" } : prev
        );
        toast("Query closed", { icon: "✓" });
      }
    },
    [queryId, setMessages, updateQueryStatus, setSelectedQuery]
  );

  const loadQueryDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await queryService.getQuery(queryId);
      setSelectedQuery(data);
    } catch (error) {
      console.error("Failed to load query:", error);
      toast.error("Failed to load query details");
    } finally {
      setLoading(false);
    }
  }, [queryId, setSelectedQuery]);

  const loadMessages = useCallback(async () => {
    try {
      const data = await queryService.getMessages(queryId);
      setMessages(data);
    } catch (error) {
      console.error("Failed to load messages:", error);
      toast.error("Failed to load messages");
    }
  }, [queryId, setMessages]);

  useEffect(() => {
    loadQueryDetails();
    loadMessages();
  }, [loadQueryDetails, loadMessages]);

  useEffect(() => {
    // Setup real-time listener
    if (user?.id && token) {
      try {
        // Ensure Echo is initialized
        initializeEcho(token);

        const channel = joinPresenceChannel(
          user.id,
          {
            onUpdate: handleRealtimeUpdate,
          },
          token
        );

        return () => {
          // channel?.unsubscribe();
        };
      } catch (error) {
        console.error("Failed to setup real-time listener:", error);
      }
    }
  }, [queryId, user?.id, token, handleRealtimeUpdate]);

  useEffect(() => {
    console.log("Messages updated in ChatWindow:", messages.length, messages);
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!messageBody.trim() && attachments.length === 0) {
      toast.error("Please enter a message or attach a file");
      return;
    }

    setSending(true);

    try {
      const formData = new FormData();
      
      // Always append body (API requires it), use empty string if no message
      const bodyText = messageBody.trim() || "";
      formData.append("body", bodyText);

      if (attachments.length > 0) {
        attachments.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
      }

      // Debug log
      console.log("=== Sending Message ===");
      console.log("Body text:", bodyText);
      console.log("Attachments count:", attachments.length);
      console.log("FormData entries:");
      for (let pair of formData.entries()) {
        console.log(pair[0], typeof pair[1] === 'object' ? pair[1].name || 'file' : pair[1]);
      }
      console.log("======================");

      await queryService.sendMessage(queryId, formData);

      setMessageBody("");
      setAttachments([]);

      // Reload messages
      await loadMessages();

      toast.success("Message sent");
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleCloseQuery = async () => {
    if (!window.confirm("Are you sure you want to close this query?")) {
      return;
    }

    try {
      const payload = { status: "CLOSED" };
      
      // Use subquery_id from location state if available, otherwise use selectedQuery.id
      const subqueryId = location.state?.subquery_id || selectedQuery?.id;
      if (subqueryId) {
        payload.subquery_id = subqueryId;
      }
      
      // Add parent_query_id if available
      const parentQueryId = location.state?.parent_query_id || selectedQuery?.parent_query_id;
      if (parentQueryId) {
        payload.parent_query_id = parentQueryId;
      }
      
      await queryService.closeQuery(queryId, payload);
      updateQueryStatus(queryId, "CLOSED");
      setSelectedQuery({ ...selectedQuery, status: "CLOSED" });
      toast.success("Query closed successfully");
      setShowActions(false);
    } catch (error) {
      console.error("Failed to close query:", error);
      toast.error("Failed to close query");
    }
  };

  const handleDeferQuery = async () => {
    if (!window.confirm("Are you sure you want to defer this query?")) {
      return;
    }

    try {
      const payload = { status: "DEFERRED" };
      
      // Use subquery_id from location state if available, otherwise use selectedQuery.id
      const subqueryId = location.state?.subquery_id || selectedQuery?.id;
      if (subqueryId) {
        payload.subquery_id = subqueryId;
      }
      
      // Add parent_query_id if available
      const parentQueryId = location.state?.parent_query_id || selectedQuery?.parent_query_id;
      if (parentQueryId) {
        payload.parent_query_id = parentQueryId;
      }
      
      await queryService.closeQuery(queryId, payload);
      updateQueryStatus(queryId, "DEFERRED");
      setSelectedQuery({ ...selectedQuery, status: "DEFERRED" });
      toast.success("Query deferred successfully");
      setShowActions(false);
    } catch (error) {
      console.error("Failed to defer query:", error);
      toast.error("Failed to defer query");
    }
  };

  const handleSearchUsers = async (searchTerm) => {
    setSearchUser(searchTerm);
    
    if (searchTerm.length >= 2) {
      try {
        const results = await queryService.searchUsers(searchTerm);
        setUserSearchResults(results);
      } catch (error) {
        console.error("Failed to search users:", error);
        toast.error("Failed to search users");
      }
    } else {
      setUserSearchResults([]);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchUser(`${user.name} (${user.email})`);
    setUserSearchResults([]);
  };

  const handleAddUser = async () => {
    if (!selectedUser) {
      toast.error("Please select a user to add");
      return;
    }

    // Check if user is already a participant
    const isAlreadyParticipant = selectedQuery?.participants?.some(
      (p) => p.user_id === selectedUser.id
    );
    
    if (isAlreadyParticipant) {
      toast.error("This user is already a participant");
      return;
    }

    // Check if selected user is the one who raised the query
    if (selectedQuery?.raised_by_user_id === selectedUser.id) {
      toast.error("This user already raised the query");
      return;
    }

    setAddingUser(true);

    try {
      await queryService.addParticipant(queryId, selectedUser.id, "cc");
      toast.success("User added successfully");
      
      // Reset add user fields
      setShowAddUser(false);
      setSelectedUser(null);
      setSearchUser("");
      setUserSearchResults([]);
      
      // Reload query details
      await loadQueryDetails();
    } catch (error) {
      console.error("Failed to add user:", error);
      toast.error(error.response?.data?.message || "Failed to add user");
    } finally {
      setAddingUser(false);
    }
  };

  const handleCancelAddUser = () => {
    setShowAddUser(false);
    setSelectedUser(null);
    setSearchUser("");
    setUserSearchResults([]);
  };

  const formatMessageDate = (date) => {
    const messageDate = new Date(date);

    if (isToday(messageDate)) {
      return format(messageDate, "HH:mm");
    } else if (isYesterday(messageDate)) {
      return `Yesterday ${format(messageDate, "HH:mm")}`;
    } else {
      return format(messageDate, "MMM dd, HH:mm");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-gradient-to-r from-orange-500 to-amber-500 text-white";
      case "REVERTED":
        return "bg-gradient-to-r from-red-500 to-rose-500 text-white";
      case "CLOSED":
        return "bg-gradient-to-r from-green-500 to-emerald-500 text-white";
      default:
        return "bg-gradient-to-r from-gray-500 to-slate-500 text-white";
    }
  };

  const canInteract = selectedQuery?.status !== "CLOSED";

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg safe-top transition-colors duration-300">
        <div className="px-6 py-5">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate("/queries")}
              className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex-1">
              <h2 className="font-bold text-xl bg-gradient-to-r from-orange-500 to-amber-600 bg-clip-text text-transparent">
                {selectedQuery?.query_reference || `Query #${queryId}`}
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-medium">
                {selectedQuery?.query_category} •{" "}
                {selectedQuery?.query_subcategory}
              </p>
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="p-2.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-95 border border-gray-200 dark:border-gray-700"
            >
              <Users className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            </button>
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-2.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-95 border border-gray-200 dark:border-gray-700"
            >
              <MoreVertical className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            </button>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <span
              className={`px-4 py-1.5 rounded-2xl text-xs font-bold shadow-md ${getStatusColor(
                selectedQuery?.status
              )}`}
            >
              {selectedQuery?.status}
            </span>
            {selectedQuery?.is_priority && (
              <span className="px-4 py-1.5 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-2xl text-xs font-bold shadow-md animate-pulse">
                Priority
              </span>
            )}
          </div>
        </div>

        {/* Details Dropdown */}
        {showDetails && (
          <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 text-gray-900 dark:text-gray-100 px-6 py-4 border-t border-gray-200 dark:border-gray-700 text-sm space-y-3 shadow-inner">
            <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-2xl shadow-sm">
              <span className="text-gray-600 dark:text-gray-300 font-medium">
                Application ID:
              </span>
              <span className="font-bold text-gray-900 dark:text-white">
                {selectedQuery?.application_id}
              </span>
            </div>
            {/* <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-2xl shadow-sm">
              <span className="text-gray-600 dark:text-gray-300 font-medium">
                Type:
              </span>
              <span className="font-bold text-gray-900 dark:text-white">
                {selectedQuery?.query_type}
              </span>
            </div> */}
            <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-2xl shadow-sm">
              <span className="text-gray-600 dark:text-gray-300 font-medium">
                Subcategory:
              </span>
              <span className="font-bold text-gray-900 dark:text-white">
                {selectedQuery?.query_subcategory}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-2xl shadow-sm">
              <span className="text-gray-600 dark:text-gray-300 font-medium">
                Created:
              </span>
              <span className="font-bold text-gray-900 dark:text-white">
                {format(
                  new Date(selectedQuery?.created_at),
                  "MMM dd, yyyy HH:mm"
                )}
              </span>
            </div>
          </div>
        )}

        {/* Actions Dropdown */}
        {showActions && (
          <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 px-6 py-3 border-t border-gray-200 dark:border-gray-700 shadow-inner space-y-2">
            {canInteract && (
              <>
                <button
                  onClick={() => {
                    setShowAddUser(true);
                    setShowActions(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-2xl transition-all text-sm font-bold shadow-lg hover:shadow-xl active:scale-95"
                >
                  <Users className="w-5 h-5" />
                  Add User to Chat
                </button>
                <button
                  onClick={handleDeferQuery}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-2xl transition-all text-sm font-bold shadow-lg hover:shadow-xl active:scale-95"
                >
                  <Clock className="w-5 h-5" />
                  Defer Query
                </button>
                <button
                  onClick={handleCloseQuery}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-2xl transition-all text-sm font-bold shadow-lg hover:shadow-xl active:scale-95"
                >
                  <XCircle className="w-5 h-5" />
                  Close Query
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add User Section */}
      {showAddUser && canInteract && (
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 px-6 py-4 border-t border-orange-200 dark:border-orange-800 shadow-inner">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-600" />
              Add User to Chat
            </h3>
            <button
              onClick={handleCancelAddUser}
              className="p-1.5 hover:bg-orange-100 dark:hover:bg-orange-800 rounded-xl transition-all"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Search Input */}
          <div className="relative mb-3">
            <input
              type="text"
              value={searchUser}
              onChange={(e) => handleSearchUsers(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 rounded-2xl border border-orange-200 dark:border-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-md"
            />
            
            {/* Search Results Dropdown */}
            {userSearchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-orange-200 dark:border-orange-700 max-h-60 overflow-y-auto z-50">
                {userSearchResults.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="w-full px-4 py-3 text-left hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected User Confirmation */}
          {selectedUser && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md border border-orange-200 dark:border-orange-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-md">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {selectedUser.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleAddUser}
                  disabled={addingUser}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl transition-all text-sm font-bold shadow-md hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingUser ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Users className="w-4 h-4" />
                      Confirm Add
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSelectedUser(null);
                    setSearchUser("");
                  }}
                  disabled={addingUser}
                  className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl transition-all text-sm font-bold shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 custom-scrollbar pb-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-indigo-600" />
              </div>
              <p className="text-gray-900 dark:text-white font-bold text-lg">
                No messages yet
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Start the conversation
              </p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => {
            const isOwnMessage = message.user_id === user?.id;
            const showDateDivider =
              index === 0 ||
              format(new Date(messages[index - 1].created_at), "yyyy-MM-dd") !==
                format(new Date(message.created_at), "yyyy-MM-dd");

            return (
              <div key={message.id}>
                {showDateDivider && (
                  <div className="flex items-center justify-center my-6">
                    <span className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-2xl shadow-md border border-gray-200 dark:border-gray-700">
                      {format(new Date(message.created_at), "MMMM dd, yyyy")}
                    </span>
                  </div>
                )}

                <div
                  className={`flex ${
                    isOwnMessage ? "justify-end" : "justify-start"
                  } animate-fade-in`}
                >
                  <div
                    className={`max-w-[80%] ${
                      isOwnMessage ? "items-end" : "items-start"
                    } flex flex-col`}
                  >
                    {!isOwnMessage && (
                      <div className="flex items-center gap-2 mb-2 px-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-md">
                          <User className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-xs text-gray-700 dark:text-gray-300 font-bold">
                          {message.user?.name}
                        </span>
                      </div>
                    )}

                    <div
                      className={`rounded-3xl px-5 py-3.5 shadow-lg overflow-hidden ${
                        isOwnMessage
                          ? "bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-br-lg"
                          : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-lg border border-gray-100 dark:border-gray-600"
                      }`}
                    >
                      {message.body && (
                        <div
                          className="text-sm break-words leading-relaxed overflow-wrap-anywhere whitespace-pre-wrap"
                          style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                          dangerouslySetInnerHTML={{
                            __html: message.safe_body_html || message.body,
                          }}
                        />
                      )}

                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <div className={`space-y-2 ${message.body ? 'mt-3 pt-3 border-t border-white/20' : ''}`}>
                            {message.attachments.map((attachment, i) => {
                              const isImage = attachment.url?.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) || 
                                             attachment.doc_type?.includes('IMAGE') ||
                                             attachment.name?.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);
                              const isPDF = attachment.url?.match(/\.pdf$/i) || 
                                           attachment.name?.match(/\.pdf$/i);
                              
                              return (
                                <a
                                  key={i}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  {isImage ? (
                                    <div className={`relative rounded-xl overflow-hidden ${isOwnMessage ? 'bg-white/10' : 'bg-gray-100 dark:bg-gray-800'} p-2`}>
                                      <img 
                                        src={attachment.url} 
                                        alt={attachment.name}
                                        className="w-full h-auto max-h-48 rounded-lg object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                      <div className="hidden w-full h-32 items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-lg">
                                        <ImageIcon className="w-8 h-8 text-gray-400" />
                                      </div>
                                    </div>
                                  ) : isPDF ? (
                                    <div className={`flex items-center gap-3 p-3 rounded-xl ${isOwnMessage ? 'bg-white/10' : 'bg-gray-100 dark:bg-gray-800'} hover:opacity-80 transition-opacity`}>
                                      <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                                        <FileText className="w-5 h-5 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-semibold truncate ${isOwnMessage ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                                          {attachment.name}
                                        </p>
                                        <p className={`text-[10px] ${isOwnMessage ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                                          PDF Document
                                        </p>
                                      </div>
                                      <Download className={`w-4 h-4 flex-shrink-0 ${isOwnMessage ? 'text-white/80' : 'text-gray-400'}`} />
                                    </div>
                                  ) : (
                                    <div className={`flex items-center gap-3 p-3 rounded-xl ${isOwnMessage ? 'bg-white/10' : 'bg-gray-100 dark:bg-gray-800'} hover:opacity-80 transition-opacity`}>
                                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                                        <Paperclip className="w-5 h-5 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-semibold truncate ${isOwnMessage ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                                          {attachment.name}
                                        </p>
                                        <p className={`text-[10px] ${isOwnMessage ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                                          {attachment.doc_type || 'Document'}
                                        </p>
                                      </div>
                                      <Download className={`w-4 h-4 flex-shrink-0 ${isOwnMessage ? 'text-white/80' : 'text-gray-400'}`} />
                                    </div>
                                  )}
                                </a>
                              );
                            })}
                          </div>
                        )}
                    </div>

                    <span
                      className={`text-xs mt-1 px-1 ${
                        isOwnMessage
                          ? "text-gray-600 dark:text-gray-400"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {formatMessageDate(message.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {canInteract ? (
        <div className="flex-shrink-0 backdrop-blur-xl bg-white/90 dark:bg-gray-900/90 border-t border-gray-200 dark:border-gray-800 px-6 py-4 shadow-lg safe-bottom transition-colors duration-300">
          {/* Attachments Preview */}
          {attachments.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-700 dark:to-gray-800 rounded-2xl text-sm shadow-md border border-indigo-100 dark:border-gray-600"
                >
                  <FileText className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-gray-700 dark:text-gray-200 font-medium max-w-[150px] truncate">
                    {file.name}
                  </span>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="p-1 hover:bg-white dark:hover:bg-gray-600 rounded-full transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input Row */}
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                navigate(
                  `/doc-upload/${selectedQuery?.application_id}/${queryId}`
                )
              }
              className="p-3.5 bg-gradient-to-br from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 rounded-2xl transition-all flex-shrink-0 shadow-lg hover:shadow-xl active:scale-95 h-[52px] w-[52px]"
            >
              <Paperclip className="w-5 h-5 text-white" />
            </button>

            <input
              ref={fileInputRef}
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="flex-1 relative">
              <textarea
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                onKeyDown={(e) => {
                  // Send message on Ctrl+Enter or Cmd+Enter
                  if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                    e.preventDefault();
                    handleSendMessage();
                  }
                  // Allow regular Enter for new line
                }}
                placeholder="Type a message..."
                rows={1}
                className="w-full px-5 py-3.5 bg-white dark:bg-gray-800 rounded-3xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-gray-900 dark:text-white shadow-md border border-gray-200 dark:border-gray-700 placeholder-gray-400 dark:placeholder-gray-500 overflow-y-auto"
                style={{ minHeight: "52px", maxHeight: "150px" }}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 150) + "px";
                }}
              />
            </div>

            <button
              onClick={handleSendMessage}
              disabled={
                sending || (!messageBody.trim() && attachments.length === 0)
              }
              className="p-3.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 shadow-lg hover:shadow-xl active:scale-95 h-[52px] w-[52px]"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0 backdrop-blur-xl bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-t border-red-200 dark:border-red-800 px-6 py-5 text-center shadow-lg safe-bottom">
          <div className="inline-flex items-center gap-2 px-5 py-3 bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-red-200 dark:border-red-800">
            <XCircle className="w-5 h-5 text-red-500" />
            <p className="text-gray-700 dark:text-gray-300 text-sm font-bold">
              This query is closed. You cannot send new messages.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatWindow;
